import { createContext, useEffect, useRef, useState } from "react";
import AdMobAPI from "../api/AdMobAPI";
import PublisherAccount from "../../types/admob/PublisherAccount";
import AccountSelectorDialog from "../../components/AccountSelectorDialog";

// const CLIENT_ID = "285936841383-vtie06d8m0lm23c5j7ljrp9j9e960ifn.apps.googleusercontent.com";
const CLIENT_ID = "482077956472-imd8noa5o1enkbvbs2skflvgkm6c7jv4.apps.googleusercontent.com";

const SCOPES =
  "https://www.googleapis.com/auth/admob.monetization https://www.googleapis.com/auth/admob.readonly https://www.googleapis.com/auth/userinfo.profile";

export interface AuthContextType {
  isSignedIn: boolean;
  isLoading: boolean;
  username?: string;
  login: () => void;
  signout: () => void;
  /** new — used by AdMobAPI */
  getAccessToken: () => Promise<string>;
  account?: PublisherAccount;
}

export const AuthContext = createContext<AuthContextType>(null!);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState<string>();
  const [account, setAccount] = useState<PublisherAccount>();
  // accounts list shown in selector when user has multiple pub accounts
  const [pendingAccounts, setPendingAccounts] = useState<PublisherAccount[] | null>(null);

  // ⏳ token cache identical to your old code
  const tokenCache = useRef<{ token: string; expiry: number } | null>(null);
  const refreshPromise = useRef<Promise<string> | null>(null);
  const tokenClient = useRef<ReturnType<
    typeof google.accounts.oauth2.initTokenClient
  > | null>(null);
  // Separate token client used ONLY for silent background refresh
  // so it never overwrites the main login callback
  const refreshClient = useRef<ReturnType<
    typeof google.accounts.oauth2.initTokenClient
  > | null>(null);

  /** 1️⃣ boot - load GIS and create token client */
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = script.defer = true;
    script.onload = () => {
      // Main login client — handles user-facing login + account fetch
      tokenClient.current = google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPES,
        callback: async (resp: any) => {
          if (resp.error) {
            console.error(resp);
            setIsLoading(false);
            return;
          }
          tokenCache.current = {
            token: resp.access_token,
            expiry: Date.now() + resp.expires_in * 1000,
          };
          // Do NOT set isSignedIn(true) here — wait until user picks an account
          // Otherwise Nav renders immediately and fires API calls before account is chosen
          setIsLoading(false);

          AdMobAPI.setTokenProvider(getAccessToken);

          try {
            const pubAccounts = (await AdMobAPI.listAccounts()).account;
            // Always show selector dialog regardless of account count
            setIsSignedIn(false);
            setPendingAccounts(pubAccounts);
          } catch (err: any) {
            // listAccounts failed (e.g. 401 — user not authorized in AdMob)
            console.error("listAccounts failed:", err);
            setIsSignedIn(false);
            setIsLoading(false);
            setPendingAccounts([]); // empty → dialog will show error state
          }
        },
      });

      // Separate silent-refresh client — ONLY used by getAccessToken()
      // Never touches the main login callback
      refreshClient.current = google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPES,
        callback: () => {}, // will be overwritten per-request in getAccessToken
      });

      const attemptSilent = () => {
        setIsLoading(true);
        tokenClient.current!.requestAccessToken({ prompt: "" }); // <= no UI
      };

      attemptSilent(); // 🔸 first page load
      setIsLoading(false);
    };
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  /** 2️⃣ login popup — shows Google account chooser so user can switch accounts */
  const login = () => {
    setIsLoading(true);
    tokenClient.current?.requestAccessToken({ prompt: "select_account" });
  };

  /** 3️⃣ sign-out = revoke + local cleanup */
  const signout = () => {
    const t = tokenCache.current?.token;
    if (t) {
      google.accounts.oauth2.revoke(t, () => {});
    }
    tokenCache.current = null;
    setIsSignedIn(false);
    setUsername(undefined);
    setAccount(undefined);
    setPendingAccounts(null);
  };

  /** Called when user picks an account from the selector dialog */
  const handleAccountSelect = (selected: PublisherAccount) => {
    setPendingAccounts(null);
    setAccount(selected);
    setIsSignedIn(true);
  };

  /** 4️⃣ AdMobAPI calls this whenever it needs a token */
  const getAccessToken = async (): Promise<string> => {
    const now = Date.now();
    if (tokenCache.current && now < tokenCache.current.expiry) {
      return tokenCache.current.token;
    }
    if (refreshPromise.current) return refreshPromise.current;

    // Use refreshClient — never overwrites the main login tokenClient callback
    refreshPromise.current = new Promise<string>((resolve, reject) => {
      refreshClient.current!.callback = (resp: any) => {
        refreshPromise.current = null;
        if (resp.error) return reject(resp);
        tokenCache.current = {
          token: resp.access_token,
          expiry: Date.now() + resp.expires_in * 1000,
        };
        resolve(resp.access_token);
      };
      refreshClient.current!.requestAccessToken({ prompt: "" }); // silent
    });
    return refreshPromise.current;
  };

  return (
    <AuthContext.Provider
      value={{
        isSignedIn,
        isLoading,
        username,
        login,
        signout,
        getAccessToken,
        account,
      }}
    >
      {/* Account selector — shown only when multiple pub accounts are found */}
      {pendingAccounts && (
        <AccountSelectorDialog
          accounts={pendingAccounts}
          onSelect={handleAccountSelect}
        />
      )}
      {children}
    </AuthContext.Provider>
  );
};
